import { useCallback } from 'react'
import { ReactComponent as BoldIcon } from 'assets/icons/bold.svg'
import { ReactComponent as ItalicIcon } from 'assets/icons/italic.svg'
import { ReactComponent as LinkIcon } from 'assets/icons/link.svg'
import { ReactComponent as UnlinkIcon } from 'assets/icons/unlink.svg'
import { ReactComponent as ListIcon } from 'assets/icons/list.svg'
import { ReactComponent as OListIcon } from 'assets/icons/olist.svg'
import { ReactComponent as YoutubeIcon } from 'assets/icons/youtube.svg'
import { ReactComponent as UndoIcon } from 'assets/icons/undo.svg'
import { ReactComponent as RedoIcon } from 'assets/icons/redo.svg'
import { ReactComponent as AlignLeftIcon } from 'assets/icons/align-left.svg'
import { ReactComponent as AlignCenterIcon } from 'assets/icons/align-center.svg'
import { ReactComponent as AlignRightIcon } from 'assets/icons/align-right.svg'
import { ReactComponent as FontColorIcon } from 'assets/icons/font-color.svg'
import { ReactComponent as UnderlineIcon } from 'assets/icons/underline.svg'

import clsx from 'clsx'
import IconMenu from 'components/IconMenu/IconMenu'
import ColorInput from '../ColorInput/ColorInput'
import TipTapImage from './TipTapImage'
const TipTapEditorBar = ({ editor, theme }) => {
  const iconColor = theme === 'light' ? '#333' : '#eee'
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    // cancelled
    if (url === null) {
      return
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()

      return
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  if (!editor) {
    return null
  }

  function getAlignmentIcon() {
    if (editor.isActive({ textAlign: 'center' })) {
      return <AlignCenterIcon fill={iconColor} width="20" />
    }
    if (editor.isActive({ textAlign: 'right' })) {
      return <AlignRightIcon fill={iconColor} width="20" />
    }
    return <AlignLeftIcon fill={iconColor} width="20" />
  }

  function getFormatValue() {
    let value = 'Paragraph'
    for (let i = 1; i < 7; i++) {
      if (editor.isActive('heading', { level: i })) {
        value = `Heading ${i}`
      }
    }

    return <span>{value}</span>
  }

  function handleImageChange(image) {
    if (image) {
      editor
        .chain()
        .focus()
        .setImage({ ...image })
        .run()
    }
  }

  function handleYoutubeClick() {
    const url = prompt('Enter YouTube URL')

    if (url) {
      editor.commands.setYoutubeVideo({
        src: url,
      })
    }
  }

  return (
    <div className="tiptap-editor-header">
      <button
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
        data-tooltip="Undo"
      >
        <UndoIcon fill={iconColor} width="20" />
      </button>
      <button
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
        data-tooltip="Redo"
      >
        <RedoIcon fill={iconColor} width="20" />
      </button>
      <div
        className={clsx('w-[1px] h-[32px] mx-2', {
          'bg-gray-bright': theme === 'dark',
          'bg-white-med': theme === 'light',
        })}
      ></div>

      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
        data-tooltip="Bold"
      >
        <BoldIcon fill={iconColor} height="15" width="15" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
        data-tooltip="Italic"
      >
        <ItalicIcon fill={iconColor} width="18" />
      </button>

      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editor.can().chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
        data-tooltip="Underline"
      >
        <UnderlineIcon fill={iconColor} height="18" />
      </button>

      <IconMenu
        containerClass="text"
        icon={getFormatValue()}
        theme={theme}
        tooltip="Format"
      >
        <button
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={clsx('text-xl', {
            'is-active': editor.isActive('paragraph'),
          })}
        >
          Paragraph
        </button>
        {[1, 2, 3, 4, 5, 6].map((i) => {
          const Header = `h${i}`
          return (
            <button
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: i }).run()
              }
              key={i}
              className={clsx(
                'relative',
                editor.isActive('heading', { level: i }) ? 'is-active' : '',
              )}
            >
              <Header>Heading {i}</Header>
            </button>
          )
        })}
      </IconMenu>

      <ColorInput
        tooltip="Text Color"
        hideInput
        hideColorPreview
        value={editor.getAttributes('textStyle').color}
        icon={<FontColorIcon fill={iconColor} width="24" />}
        onChange={(value) => editor.chain().focus().setColor(value).run()}
      />

      <IconMenu icon={getAlignmentIcon()} tooltip="Align">
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
        >
          <AlignLeftIcon fill="#eee" width="20" /> <span>Left</span>
        </button>
        <button
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign('center')
              .setImageAlign('center')
              .run()
          }
          className={
            editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''
          }
        >
          <AlignCenterIcon fill="#eee" width="20" /> <span>Center</span>
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
        >
          <AlignRightIcon fill="#eee" width="20" /> <span>Right</span>
        </button>
      </IconMenu>

      <div
        className={clsx('w-[1px] h-[32px] mx-2', {
          'bg-gray-bright': theme === 'dark',
          'bg-white-med': theme === 'light',
        })}
      ></div>

      <button
        onClick={setLink}
        className={editor.isActive('link') ? 'is-active' : ''}
        data-tooltip="Link"
      >
        {editor.isActive('link') ? (
          <UnlinkIcon fill={iconColor} width="20" />
        ) : (
          <LinkIcon fill={iconColor} width="20" />
        )}
      </button>

      <TipTapImage
        value={editor.getAttributes('customImage')}
        onChange={handleImageChange}
        theme={theme}
      />

      <button onClick={handleYoutubeClick} data-tooltip="YouTube Video">
        <YoutubeIcon fill={iconColor} width="22" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        data-tooltip="Bullet List"
      >
        <ListIcon fill={iconColor} width="20" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        data-tooltip="Numbered List"
      >
        <OListIcon fill={iconColor} width="20" />
      </button>
    </div>
  )
}

export default TipTapEditorBar
